/**
 * dictation/client — post one spoken segment, get the words back.
 *
 * Same transport idiom as the readout's client and the session client: the
 * Cloud Functions base URL, a Firebase ID token as a Bearer header, and a
 * timeout so a hung request cannot wedge the state machine while somebody is
 * mid-sentence.
 *
 * `firebase/auth` is imported LAZILY, inside the one call that needs a token
 * (2026-09-22). Importing it at module scope touches global `fetch` AT IMPORT
 * TIME, so every component that reached this module — now including the thread
 * search box, which grew a microphone — dragged that into its test environment
 * and took the suite down before a single test ran. It is the load-time trap
 * CLAUDE.md names, and the honest fix is here rather than a `jest.mock` in
 * each suite: nothing needs an auth SDK until somebody actually speaks.
 */

const CF_BASE_URL = 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net';

/** One segment is a sentence or two; past this the speaker has moved on. */
const DICTATION_TIMEOUT_MS = 25_000;

export interface DictationResponse {
  ok: boolean;
  text: string;
  reason?: string;
  notice?: string;
}

export interface DictationRequest {
  threadId: string;
  /** Base64 of a complete 16 kHz mono WAV. */
  audio: string;
  language?: string;
  signal?: AbortSignal;
}

const TRANSPORT_FAILURE: DictationResponse = { ok: false, text: '', reason: 'transport' };

export async function transcribeDictation(req: DictationRequest): Promise<DictationResponse> {
  let user: { getIdToken: (force?: boolean) => Promise<string> } | null = null;
  try {
    const { getAuth } = await import('firebase/auth');
    user = getAuth().currentUser;
  } catch {
    return TRANSPORT_FAILURE;
  }
  if (!user) return { ...TRANSPORT_FAILURE, reason: 'unauthenticated' };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), DICTATION_TIMEOUT_MS);
  const onAbort = () => controller.abort();
  req.signal?.addEventListener('abort', onAbort, { once: true });

  try {
    const token = await user.getIdToken(false);
    const response = await fetch(`${CF_BASE_URL}/prest0nTranscribeDictation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        threadId: req.threadId,
        audio: req.audio,
        mimeType: 'audio/wav',
        ...(req.language ? { language: req.language } : {}),
      }),
      signal: controller.signal,
    });
    const body = (await response.json().catch(() => null)) as DictationResponse | null;
    if (!body || typeof body !== 'object') return TRANSPORT_FAILURE;
    return { ...body, ok: body.ok === true && response.ok, text: String(body.text || '') };
  } catch {
    return TRANSPORT_FAILURE;
  } finally {
    clearTimeout(timer);
    req.signal?.removeEventListener('abort', onAbort);
  }
}
